import { useEffect, useState } from "react";
import { Numeric } from "@wmart/ui";
import { api } from "../../lib/api-client.js";
import { formatGBP } from "../../lib/format.js";
import type { CashbackSummaryResponse } from "@wmart/shared-types";

export interface LifetimeCashbackStatProps {
  customerId: string;
}

type State =
  | { kind: "loading" }
  | { kind: "ready"; data: CashbackSummaryResponse }
  | { kind: "error" };

/**
 * Per-customer lifetime cashback total — sum of all successfully-settled
 * cashback refunds. Hides at count=0 is NOT applied (admin always sees
 * the stat, even at £0).
 */
export function LifetimeCashbackStat({ customerId }: LifetimeCashbackStatProps) {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    api
      .get<CashbackSummaryResponse>(
        `/v1/admin/customers/${customerId}/cashback-summary`,
      )
      .then((data) => {
        if (!cancelled) setState({ kind: "ready", data });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  return (
    <div className="flex flex-col gap-1">
      <p className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
        Lifetime cashback
      </p>
      {state.kind === "loading" ? (
        <p className="text-body-sm text-ink-500">Loading…</p>
      ) : null}
      {state.kind === "error" ? (
        <p className="text-body-sm text-danger">
          Unable to load cashback summary.
        </p>
      ) : null}
      {state.kind === "ready" ? (
        <>
          <p className="text-title-md text-ink-900">
            <Numeric serif>{formatGBP(state.data.total_minor)}</Numeric>
          </p>
          <p className="text-body-sm text-ink-500">
            {state.data.count} {state.data.count === 1 ? "award" : "awards"}
          </p>
        </>
      ) : null}
    </div>
  );
}
